import { Link } from "react-router";
import {
  EDITION_BT_LINK,
  EDITION_BTG_LINK,
} from "@features/banner/Edition/utils/constants";
import { usePreferredEdition } from "@hooks/usePreferredEdition";
import { cn, getUrlQuery } from "@util/helpers";

type EditionProps = {
  /** This is the classname of the component. */
  className?: string;
  /** Flag to show/hide label. */
  showLabel?: boolean;
  /** This decides the color scheme of text. */
  theme?: "light" | "dark";
  /** The ref code to be added the links. */
  editionRef?: string;
};

/** This is the edition switcher component. */
const Edition = ({
  className,
  showLabel = false,
  theme = "light",
  editionRef,
}: EditionProps): React.ReactElement => {
  const { isSgPreferred, handleSG, handleNONSG } = usePreferredEdition();

  const isLightTheme = theme === "light";
  const isDarkTheme = theme === "dark";

  const linkParams = new URLSearchParams({
    ref: editionRef || "header-edition-switch",
  });
  const btEditionQuery = getUrlQuery(EDITION_BT_LINK, linkParams);
  const btgEditionQuery = getUrlQuery(EDITION_BTG_LINK, linkParams);

  return (
    <div
      data-testid="edition-component"
      className={cn(
        "w-full font-poppins text-4xs font-medium tracking-tightest text-gray-550",
        className
      )}
    >
      {showLabel ? (
        <p className="mb-1 text-2xs font-semibold uppercase text-gray-350">
          CHOOSE EDITION
        </p>
      ) : null}

      <div
        className={cn("flex flex-row items-center justify-center gap-2", {
          "justify-start gap-1 rounded-[4px] bg-gray-150 p-1": showLabel,
        })}
      >
        <Link
          to={EDITION_BT_LINK + btEditionQuery}
          className={cn(
            "inline-block px-[.38rem] font-medium tracking-[1px] text-gray-550",
            {
              "hover:bg-gray-250": isLightTheme,
              "hover:bg-gray-850": isDarkTheme,
              "pointer-events-none hover:bg-transparent": isSgPreferred,
              "text-gray-850": isSgPreferred && isLightTheme,
              "text-gray-125": isSgPreferred && isDarkTheme,
              "flex-1 rounded-[4px] py-3 text-center text-2xs font-semibold leading-none tracking-2% text-gray-515 hover:bg-white hover:no-underline":
                showLabel,
              "bg-white text-gray-850":
                showLabel && isSgPreferred && isLightTheme,
            }
          )}
          onClick={handleSG}
          reloadDocument
        >
          Singapore
        </Link>

        <Link
          to={EDITION_BTG_LINK + btgEditionQuery}
          className={cn(
            "inline-block px-[.38rem] font-medium tracking-[1px] text-gray-550",
            {
              "hover:bg-gray-250": isLightTheme,
              "hover:bg-gray-850": isDarkTheme,
              "pointer-events-none hover:bg-transparent": !isSgPreferred,
              "text-gray-850": !isSgPreferred && isLightTheme,
              "text-gray-125": !isSgPreferred && isDarkTheme,
              "flex-1 rounded-[4px] py-3 text-center text-2xs font-semibold leading-none tracking-2% text-gray-515 hover:bg-white hover:no-underline":
                showLabel,
              "bg-white text-gray-850":
                showLabel && !isSgPreferred && isLightTheme,
            }
          )}
          onClick={handleNONSG}
          reloadDocument
        >
          Global
        </Link>
      </div>
    </div>
  );
};

export default Edition;
